import { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useUpdateEffect, useWindowSize } from 'react-use';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';

import { ListingServices } from '@services';
import { useTranslation, useFetchProducts } from '@hooks';
import { convertCategoryName } from '@utils';
import { FilterProvider } from '@context/FilterContext';

import * as gtag from 'lib/gtag';
import { ListingLayout, ProductCard } from '@components/listing';

const SearchNotFound = dynamic(() =>
    import('components/listing/SearchNotFound'),
);

export const LISTINGTYPES = {
    single: {
        name: 'single',
        column: 'repeat(1, 1fr)',
        count: 1,
        row: '532px',
        width: '100vw',
        height: 'calc((100vw - 3px) / 1 * 1.5)',
    },
    double: {
        name: 'double',
        column: 'repeat(2, 1fr)',
        row: '532px',
        count: 2,
        width: '50vw',
        height: 'calc((100vw - 3px) / 2 * 1.5)',
    },
    quartet: {
        name: 'quartet',
        column: 'repeat(4, 1fr)',
        row: '723px',
        count: 4,
        width: '24.99vw',
        height: 'calc((100vw - 3px) / 4 * 1.5)',
    },
    octal: {
        name: 'octal',
        column: 'repeat(8, 1fr)',
        row: '372px',
        count: 8,
        width: '12.48vw',
        height: 'calc((100vw - 3px) / 8 * 1.5)',
    },
};

const Category = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const loadMore = useRef(null);
    const { category, searchQuery } = router.query;
    const { width } = useWindowSize();
    const [listingType, setMobileListingType] = useState(LISTINGTYPES.double);
    const productQuery = router.query;
    productQuery.pageSize = listingType.count * 5;

    const changeListingType = (listView) => {
        switch (listView) {
            case 'single':
                return setMobileListingType(LISTINGTYPES.single);
            case 'double':
                return setMobileListingType(LISTINGTYPES.double);
            case 'quartet':
                return setMobileListingType(LISTINGTYPES.quartet);
            case 'octal':
                return setMobileListingType(LISTINGTYPES.octal);
            default:
                return width > 1024
                    ? setMobileListingType(LISTINGTYPES.quartet)
                    : setMobileListingType(LISTINGTYPES.double);
        }
    };

    useEffect(() => {
        if (width > 1024) {
            if (
                !(
                    listingType.name === 'quartet' ||
                    listingType.name === 'octal'
                )
            ) {
                setMobileListingType(LISTINGTYPES.quartet);
            }
        } else if (
            !(listingType.name === 'single' || listingType.name === 'double')
        ) {
            setMobileListingType(LISTINGTYPES.double);
        }
    }, [width]);

    const {
        data,
        hasNextPage,
        isLoading,
        pageMeta,
        fetchNextPage,
    } = useFetchProducts(router.query, loadMore);

    useUpdateEffect(() => {
        if (!isLoading && pageMeta) {
            gtag.event({
                action: 'view_item_list',
                items: data?.pages[0].data.map((product) => product),
            });
        }
    }, [pageMeta]);

    const canonical =
        data?.pageParams.length >= 2
            ? `${t('canonicalText')} ${pageMeta?.next_page}`
            : '';

    if (pageMeta?.total < 1 && !isLoading && searchQuery) {
        return <SearchNotFound />;
    }

    const isFilterActive = Object.keys(router.query).some((key) =>
        ['colour', 'q', 'keyword'].includes(key),
    );

    return (
        <>
            <NextSeo
                title={`${`Fervente | ${convertCategoryName(
                    category,
                    true,
                )}`} - ${t('newSeasonText')}`}
                description={t('allCategoriesMetaDesc')}
                noindex={isFilterActive}
                canonical={`https://www.ferventeshop.com/${category}${canonical}`}
                openGraph={{
                    type: 'category',
                    url: `https://www.ferventeshop.com/${category}`,
                    title: `${`${t('ferventeTitle')} ${convertCategoryName(
                        category,
                        true,
                    )} - ${t('newSeasonText')}`}`,
                    description: t(`allCategoriesMetaDesc`),
                    site_name: 'ferventeshop.com',
                    images: [
                        {
                            url:
                                'https://cloud.keikei.com/cdn/fervente/favicon.ico',
                            width: 800,
                            height: 600,
                            alt: 'Fervente',
                        },
                    ],
                }}
            />
            <FilterProvider page={pageMeta?.next_page}>
                <Suspense>
                    <ListingLayout
                        category={category}
                        categoryDescription={
                            data?.pages[0].aggregations?.category_description
                        }
                        hasNextProducts={hasNextPage}
                        fetchNextPage={fetchNextPage}
                        loadMore={loadMore}
                        listStyle={LISTINGTYPES.default}
                        isLoading={isLoading}
                        productCount={pageMeta?.total}
                        listingType={listingType}
                        changeListingType={changeListingType}
                    >
                        {data?.pages &&
                            data?.pages.map((page, pageIndex) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Fragment key={`page-${pageIndex}`}>
                                    {page?.data?.map((product, i) => (
                                        <ProductCard
                                            key={`product-${product.id}`}
                                            product={product}
                                            priority={i < 1 && pageIndex === 0}
                                            isProdRequest={
                                                category ===
                                                'production-request'
                                            }
                                            border={
                                                i < listingType.count &&
                                                pageIndex === 0
                                            }
                                            listingType={listingType}
                                        />
                                    ))}
                                </Fragment>
                            ))}
                    </ListingLayout>
                </Suspense>
            </FilterProvider>
        </>
    );
};

export async function getStaticProps() {
    // const queryClient = new QueryClient();
    // await queryClient.prefetchInfiniteQuery(
    //     ['products'],
    //     ListingServices.getProducts({ pageParam: 1 }),
    // );

    // queryClient.prefetchQuery(['posts'], getPosts);
    return {
        props: {},
    };
}

export async function getStaticPaths({ locales }) {
    const data = await ListingServices.getProductCategories();
    const slugs = locales.reduce((arr, locale) => {
        const langLocale = locale === 'tr' ? 'tr' : 'en';
        data?.data.forEach((category) => {
            if (category.slug[langLocale]) {
                arr.push(`/${locale}/${category.slug[langLocale]}`);
            }
        });
        return arr;
    }, []);

    const paths = slugs.map((slug) => slug);
    return { paths, fallback: false };
}

export default Category;
